import React from "react";
import {
  Box, Button, Dialog, IconButton, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ColorPicker from "../../../../Components/ColorPicker/ColorPicker";
import IconPicker from "../../../../Components/IconPicker/IconPicker";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";

// Batch 7 §7.4 — small centred modal for renaming a phase.
// One row: [ColorPicker] [IconPicker] [free-text Label input pre-selected].
// Footer: [REMOVE FROM WORKFLOW (red, left)]  [CANCEL] [SAVE]
//
// The DB column mastery_workflow_phases.phaseName already accepts free
// text — no migration. The current editor constrains it via a Select at
// Settings/Workflows/Editor/index.tsx:504-514 (the hardcoded 5 options).
// The new PhaseCard uses this modal instead, so that Select is gone.

export type EditPhaseValue = {
  phaseName: string;
  colour: string;
  icon: string | null;
};

export type EditPhaseModalProps = {
  open: boolean;
  value: EditPhaseValue;
  onCancel: () => void;
  onSave: (next: EditPhaseValue) => void;
  onRemove: () => void;
  canRemove: boolean;                 // false if any target sits in this phase
  removeBlockedReason?: string | null;
};

export default function EditPhaseModal({
  open, value, onCancel, onSave, onRemove, canRemove, removeBlockedReason,
}: EditPhaseModalProps) {
  const [draft, setDraft] = React.useState<EditPhaseValue>(value);
  const labelRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(value);
      // Pre-select the label so typing replaces it immediately (§7.4).
      setTimeout(() => {
        labelRef.current?.focus();
        labelRef.current?.select();
      }, 0);
    }
  }, [open, value]);

  const canSave = draft.phaseName.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 520, maxWidth: "calc(100vw - 32px)",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3, py: 2,
          display: "flex", alignItems: "center", gap: 1,
          borderBottom: `1px solid ${LegacyTokens.line}`,
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
          Edit phase
        </Typography>
        <IconButton onClick={onCancel} aria-label="Close" sx={{ color: LegacyTokens.ink3 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <Box>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.75, display: "block" }}>Colour</Typography>
            <ColorPicker
              value={draft.colour || LegacyTokens.blue}
              onChange={(hex) => setDraft((d) => ({ ...d, colour: hex }))}
              size={56}
              ariaLabel="Phase colour"
            />
          </Box>
          <Box>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.75, display: "block" }}>Icon</Typography>
            <IconPicker
              value={draft.icon}
              onChange={(name) => setDraft((d) => ({ ...d, icon: name }))}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.5, display: "block" }}>Label</Typography>
            <TextField
              fullWidth variant="standard"
              value={draft.phaseName}
              inputRef={labelRef}
              onChange={(e) => setDraft((d) => ({ ...d, phaseName: e.target.value }))}
              InputProps={{
                sx: {
                  fontSize: 18, fontWeight: 500, color: LegacyTokens.ink,
                  "&:after": { borderBottomColor: B13.primary },
                },
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3, py: 1.5,
          borderTop: `1px solid ${LegacyTokens.line}`,
          display: "flex", alignItems: "center",
          backgroundColor: LegacyTokens.surface,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Button
            onClick={() => canRemove ? onRemove() : undefined}
            disabled={!canRemove}
            title={!canRemove ? (removeBlockedReason ?? "Cannot remove") : undefined}
            sx={{
              textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
              color: "#D32F2F",
              "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.06)" },
              "&.Mui-disabled": { color: "rgba(0,0,0,0.26)" },
            }}
          >
            Remove from workflow
          </Button>
        </Box>
        <Button
          onClick={onCancel}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
            color: "#D32F2F",
            "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSave(draft)}
          disabled={!canSave}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
            color: "#1A73E8",
            "&:hover": { backgroundColor: "rgba(26, 115, 232, 0.06)" },
            "&.Mui-disabled": { color: "rgba(0,0,0,0.26)" },
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
}
